import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { meApi, workerRequestApi, safetyKitApi, stationeryApi, itApi, materialUnitApi, siteApi, siteCorrectionApi } from '../api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import MaterialInfoPanel from '../components/common/MaterialInfoPanel';
import SearchableSelect from '../components/common/SearchableSelect';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { buildCycles } from '../constants/deductionCycles';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const currency = (v) =>
  v != null ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Unified transaction feed — same shape of event Site History's Transactions
// tab shows (site assigned/transferred, materials/stationery issued/
// returned), just scoped to this one worker instead of a site. Built
// client-side from the same underlying data the other tabs already use, so
// there's no separate source of truth to keep in sync.
function buildTransactions(worker) {
  const rows = [];

  for (const log of worker.assignmentHistory || []) {
    if (log.action === 'ASSIGN') {
      rows.push({
        id: `wa-${log.id}`, txType: 'site_assigned', date: log.date,
        site: log.siteName, itemName: null, category: null, quantity: null,
        unitPrice: null, value: null, status: 'assigned', performedBy: log.performedByUsername,
      });
    } else {
      rows.push({
        id: `wt-${log.id}`, txType: 'site_transferred', date: log.date,
        site: log.siteName, itemName: null, category: null, quantity: null,
        unitPrice: null, value: null, status: 'transferred', performedBy: log.performedByUsername,
      });
    }
  }

  for (const k of worker.kits || []) {
    const price = parseFloat(k.kit?.price || 0);
    rows.push({
      id: `mi-${k.id}`, txType: 'material_issued', date: k.issuedDate,
      site: k.inventory?.site?.name, itemName: k.kit?.name, category: k.kit?.type || null, quantity: k.quantity,
      unitPrice: price, value: price * (k.quantity || 0), status: k.isReturned ? 'returned' : 'active',
      performedBy: k.issuedByUsername,
      materialUnitId: k.materialUnit?.id || null, materialIdCode: k.materialUnit?.materialIdCode || null,
    });
    if ((k.returnedQuantity || 0) > 0) {
      rows.push({
        id: `mr-${k.id}`, txType: 'material_returned', date: k.returnedDate,
        site: k.inventory?.site?.name, itemName: k.kit?.name, category: k.kit?.type || null, quantity: k.returnedQuantity,
        unitPrice: price, value: price * k.returnedQuantity, status: 'returned',
        performedBy: k.issuedByUsername,
        materialUnitId: k.materialUnit?.id || null, materialIdCode: k.materialUnit?.materialIdCode || null,
      });
    }
  }

  for (const s of worker.stationeryIssuances || []) {
    const price = parseFloat(s.item?.price || 0);
    rows.push({
      id: `si-${s.id}`, txType: 'stationery_issued', date: s.issuedDate,
      site: s.site?.name, itemName: s.item?.name, category: s.item?.category, quantity: s.quantity,
      unitPrice: price, value: price * (s.quantity || 0),
      status: s.isReturned
        ? (s.repairStatus === 'UNDER_REPAIR' ? 'under_repair' : s.repairStatus === 'SCRAPPED' ? 'scrapped' : 'returned')
        : 'active',
      performedBy: s.issuedByUsername,
      materialUnitId: s.materialUnit?.id || null, materialIdCode: s.materialUnit?.materialIdCode || null,
    });
    if (s.isReturned) {
      let status;
      if (s.repairStatus === 'UNDER_REPAIR') status = 'under_repair';
      else if (s.repairStatus === 'REPAIRED') status = 'repaired';
      else if (s.repairStatus === 'SCRAPPED') status = 'scrapped';
      else status = 'returned';
      rows.push({
        id: `sr-${s.id}`, txType: 'stationery_returned', date: s.returnedDate,
        site: s.site?.name, itemName: s.item?.name, category: s.item?.category, quantity: s.returnedQty,
        unitPrice: price, value: price * (s.returnedQty || 0), status,
        performedBy: s.issuedByUsername,
        materialUnitId: s.materialUnit?.id || null, materialIdCode: s.materialUnit?.materialIdCode || null,
      });
    }
  }

  return rows.sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Labels reflect the employee's own perspective, not the storekeeper's — an
// employee receives materials/stationery, they don't issue them (the
// storekeeper does). "Returned" stays as-is since returning is genuinely the
// employee's own action.
const TX_TYPE_CFG = {
  site_assigned:       { label: 'Site Assigned',        color: 'blue' },
  site_transferred:    { label: 'Site Transferred',     color: 'purple' },
  material_issued:     { label: 'Materials Received',   color: 'indigo' },
  material_returned:   { label: 'Material Returned',    color: 'gray' },
  stationery_issued:   { label: 'Stationery Received',  color: 'green' },
  stationery_returned: { label: 'Stationery Returned',  color: 'gray' },
};

const STATUS_CFG = {
  active:       'bg-green-100 text-green-700',
  returned:     'bg-gray-100 text-gray-600',
  under_repair: 'bg-amber-100 text-amber-700',
  repaired:     'bg-teal-100 text-teal-700',
  scrapped:     'bg-red-100 text-red-700',
  assigned:     'bg-sky-100 text-sky-700',
  transferred:  'bg-teal-100 text-teal-700',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_CFG[status] || 'bg-gray-100 text-gray-500'}`}>
    {status ? status.replace(/_/g, ' ') : '—'}
  </span>
);

export default function MyProfile() {
  const { user, hasPermission } = useAuth();
  const [worker, setWorker]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState('overview');
  const [deductionCycleFilter, setDeductionCycleFilter] = useState('current'); // 'current' | 'all' | a cycleStart date string
  const deductionCycles = useMemo(() => buildCycles(6), []);

  // ── My Requests — employee-initiated Material/Stationery requests. Raising
  // one is optional and never blocks the normal issue flow; see the auto-close
  // hook in workerKits.js/stationery.js. Distinct from Requirement (a separate
  // site-vs-central-store procurement concept). ─────────────────────────────
  const [myRequests, setMyRequests]           = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(true);
  const [showNewRequest, setShowNewRequest]   = useState(false);
  const [newRequestForm, setNewRequestForm]   = useState({ itemKind: 'MATERIAL', itemId: '', quantity: 1, remarks: '' });
  const [newRequestError, setNewRequestError] = useState('');
  const [newRequestSaving, setNewRequestSaving] = useState(false);
  const [catalogMaterials, setCatalogMaterials] = useState([]);
  const [catalogStationery, setCatalogStationery] = useState([]);
  const [catalogIT, setCatalogIT] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');

  // "My site is wrong" — small info popover near the site name, opening into
  // a report modal. `siteCorrection` is my own current pending claim (or
  // null) — its mere presence means "awaiting confirmation"; there's no
  // separate status flag to fall out of sync with that.
  const [sites, setSites] = useState([]);
  const [siteCorrection, setSiteCorrection] = useState(null);
  const [siteInfoOpen, setSiteInfoOpen] = useState(false);
  const [siteCorrectionOpen, setSiteCorrectionOpen] = useState(false);
  const [claimedSiteId, setClaimedSiteId] = useState('');
  const [siteCorrectionSaving, setSiteCorrectionSaving] = useState(false);
  const [siteCorrectionError, setSiteCorrectionError] = useState('');
  const [siteCorrectionConfirmOpen, setSiteCorrectionConfirmOpen] = useState(false);
  const siteInfoRef = useRef(null);

  const loadSiteCorrection = () => {
    siteCorrectionApi.myStatus().then((r) => setSiteCorrection(r.data.data)).catch(() => {});
  };

  useEffect(() => {
    if (!siteInfoOpen) return;
    const handler = (e) => { if (siteInfoRef.current && !siteInfoRef.current.contains(e.target)) setSiteInfoOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [siteInfoOpen]);

  const openSiteCorrection = () => {
    setSiteInfoOpen(false);
    setClaimedSiteId('');
    setSiteCorrectionError('');
    setSiteCorrectionOpen(true);
    if (sites.length === 0) siteApi.list().then((r) => setSites(r.data.data || [])).catch(() => {});
  };

  // Picking a site and hitting Submit only opens the in-app confirm step —
  // the actual report doesn't go out until that's confirmed.
  const handleSiteCorrectionSubmit = (e) => {
    e.preventDefault();
    if (!claimedSiteId) return;
    setSiteCorrectionConfirmOpen(true);
  };

  const handleSiteCorrectionConfirmed = async () => {
    setSiteCorrectionSaving(true);
    setSiteCorrectionError('');
    try {
      await siteCorrectionApi.create({ claimedSiteId: parseInt(claimedSiteId) });
      setSiteCorrectionConfirmOpen(false);
      setSiteCorrectionOpen(false);
      loadSiteCorrection();
    } catch (err) {
      setSiteCorrectionConfirmOpen(false);
      setSiteCorrectionError(err.response?.data?.message || 'Failed to submit');
    }
    setSiteCorrectionSaving(false);
  };

  // Material ID lifecycle history modal (same pattern as InventoryPage.jsx)
  const [historyModal, setHistoryModal] = useState(null); // { unit, events } | 'loading'
  const openMaterialHistory = async (materialUnitId) => {
    setHistoryModal('loading');
    try {
      const res = await materialUnitApi.history(materialUnitId);
      setHistoryModal(res.data.data);
    } catch {
      setHistoryModal(null);
    }
  };

  const loadMyRequests = () => {
    setMyRequestsLoading(true);
    workerRequestApi.list({ mine: 'true' })
      .then((res) => setMyRequests(res.data.data || []))
      .catch(() => {})
      .finally(() => setMyRequestsLoading(false));
  };

  // Click-and-drag horizontal scrolling for the transactions table — same
  // pattern as Site History's Transactions table.
  const txScrollRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, scrollLeft: 0 });

  const handleTxMouseDown = useCallback((e) => {
    const el = txScrollRef.current;
    if (!el) return;
    dragRef.current = { dragging: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
  }, []);

  const handleTxMouseMove = useCallback((e) => {
    const state = dragRef.current;
    const el = txScrollRef.current;
    if (!state.dragging || !el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - state.startX;
    el.scrollLeft = state.scrollLeft - walk;
  }, []);

  const handleTxMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  useEffect(() => {
    meApi.getProfile()
      .then((r) => setWorker(r.data.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMyRequests(); }, []); // eslint-disable-line
  useEffect(() => { loadSiteCorrection(); }, []); // eslint-disable-line

  const openNewRequest = () => {
    setNewRequestForm({ itemKind: 'MATERIAL', itemId: '', quantity: 1, remarks: '' });
    setNewRequestError('');
    setItemSearchQuery('');
    setShowNewRequest(true);
    if (catalogMaterials.length === 0) safetyKitApi.list({ isActive: true }).then((r) => setCatalogMaterials(r.data.data || [])).catch(() => {});
    if (catalogStationery.length === 0) stationeryApi.listItems({ isActive: true }).then((r) => setCatalogStationery(r.data.data || [])).catch(() => {});
    // IT's catalog is only readable by someone whose group holds it_view (IT
    // is deliberately isolated, unlike Materials/Stationery which are open to
    // any authenticated user) — skip the fetch entirely for anyone who'd just
    // get a 403 back, matching how the IT tab itself is hidden for them below.
    if (hasPermission('it_view') && catalogIT.length === 0) itApi.listItems({ isActive: true }).then((r) => setCatalogIT(r.data.data || [])).catch(() => {});
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setNewRequestSaving(true);
    setNewRequestError('');
    try {
      const idField = newRequestForm.itemKind === 'MATERIAL'
        ? { kitId: parseInt(newRequestForm.itemId) }
        : newRequestForm.itemKind === 'STATIONERY'
        ? { stationeryItemId: parseInt(newRequestForm.itemId) }
        : { itItemId: parseInt(newRequestForm.itemId) };
      await workerRequestApi.create({
        itemKind: newRequestForm.itemKind,
        ...idField,
        quantity: parseInt(newRequestForm.quantity) || 1,
        remarks: newRequestForm.remarks || undefined,
      });
      setShowNewRequest(false);
      loadMyRequests();
    } catch (err) {
      setNewRequestError(err.response?.data?.message || 'Failed to raise request');
    } finally {
      setNewRequestSaving(false);
    }
  };

  const handleWithdrawRequest = async (id) => {
    try { await workerRequestApi.withdraw(id); loadMyRequests(); }
    catch { /* best-effort — the row simply won't update if this fails */ }
  };

  if (loading) return <LoadingSpinner />;

  if (error || !worker) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        </div>
        <p className="text-gray-600 font-medium">No employee record linked</p>
        <p className="text-gray-400 text-sm mt-1">{error}</p>
        <p className="text-gray-400 text-xs mt-3">Contact your administrator to link your account to a worker record using your Employee ID.</p>
      </div>
    );
  }

  const siteAssignments = worker.siteAssignments || [];
  const allKits      = worker.kits || [];
  const allStat      = worker.stationeryIssuances || [];
  const deductions   = worker.salaryDeductions || [];

  const activeKits   = allKits.filter((k) => !k.isReturned);
  const returnedKits = allKits.filter((k) => k.isReturned);

  const activeStat   = allStat.filter((s) => !s.isReturned);
  const returnedStat = allStat.filter((s) => s.isReturned);

  const totalDeductions = deductions.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

  // ── Deduction cycle filter (Deductions tab) ───────────────────────────────
  const currentCycleStart = deductionCycles[0]?.cycleStart;
  const currentCycleDeductions = deductions.filter(
    (d) => d.cycleStart && new Date(d.cycleStart).toISOString().slice(0, 10) === currentCycleStart
  );
  const currentCycleTotal = currentCycleDeductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  const displayedDeductions = deductionCycleFilter === 'all'
    ? deductions
    : deductionCycleFilter === 'current'
      ? currentCycleDeductions
      : deductions.filter(
          (d) => d.cycleStart && new Date(d.cycleStart).toISOString().slice(0, 10) === deductionCycleFilter
        );

  // Sum outstanding/returned quantity, not record count — a single record can
  // represent more than one unit (e.g. a multi-unit consumable issuance), so
  // counting records under- or over-reports how many units were actually
  // issued/returned relative to the per-row "Qty" figures shown below.
  const activeKitsQty     = activeKits.reduce((s, k) => s + (k.quantity - (k.returnedQuantity || 0)), 0);
  const activeStatQty     = activeStat.reduce((s, i) => s + i.quantity, 0);
  const returnedKitsQty   = returnedKits.reduce((s, k) => s + (k.returnedQuantity || k.quantity), 0);
  const returnedStatQty   = returnedStat.reduce((s, i) => s + (i.returnedQty || i.quantity), 0);

  const transactions = buildTransactions(worker);

  const tabs = [
    { key: 'overview',     label: 'Overview' },
    { key: 'materials',    label: `Materials (${allKits.length})` },
    { key: 'stationery',   label: `Stationery (${allStat.length})` },
    { key: 'deductions',   label: `Deductions (${deductions.length})` },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-5">

      {/* ── Profile header ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center gap-5">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
        >
          {(worker.name || user?.username)?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900">{worker.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            {worker.employeeId && <span className="text-sm text-gray-500 font-mono">{worker.employeeId}</span>}
            {worker.role       && <span className="text-sm text-gray-500">{worker.role}</span>}
            {siteAssignments.map((a) => (
              <span key={a.siteId ?? a.id} className="text-sm text-gray-500">
                {a.site?.name}{a.assignmentType === 'GUEST' ? ' (Guest)' : ''}
              </span>
            ))}
            <div className="relative inline-flex items-center" ref={siteInfoRef}>
              <button
                type="button"
                onClick={() => setSiteInfoOpen((o) => !o)}
                className="text-gray-400 hover:text-gray-600"
                title="Site info"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
              {siteInfoOpen && (
                <div className="absolute left-0 top-6 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={openSiteCorrection}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Report wrong site
                  </button>
                </div>
              )}
            </div>
            {siteCorrection && (
              <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                Awaiting confirmation
              </span>
            )}
          </div>
        </div>
        <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${worker.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {worker.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* ── Stats row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Active Materials" value={activeKitsQty}  color="indigo" />
        <StatCard label="Active Stationery" value={activeStatQty} color="purple" />
        <StatCard label="Total Returned"   value={returnedKitsQty + returnedStatQty} color="green" />
        <StatCard label="Total Deductions" value={currency(totalDeductions)} color="red" />
      </div>

      {/* ── My Requests — optional, employee-initiated Material/Stationery
          requests. Raising one never blocks the normal issue flow. Shown up
          top so an employee sees their own pending/fulfilled requests
          immediately, without scrolling past the tabbed history below. ───── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-800">My Requests</h2>
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
              {myRequests.length}
            </span>
          </div>
          <button className="btn-primary !py-1.5 !px-3 !text-sm" onClick={openNewRequest}>+ Request Item</button>
        </div>
        <div className="p-5">
          {myRequestsLoading ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
          ) : myRequests.length === 0 ? (
            <Empty text="No requests raised yet — requesting is optional." />
          ) : (
            <div className="space-y-2">
              {myRequests.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-sm text-gray-900">{r.kit?.name || r.stationeryItem?.name || r.itItem?.name}</p>
                      <Badge
                        label={r.itemKind === 'MATERIAL' ? 'Material' : r.itemKind === 'STATIONERY' ? 'Stationery' : 'IT'}
                        color={r.itemKind === 'MATERIAL' ? 'indigo' : r.itemKind === 'STATIONERY' ? 'green' : 'purple'}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Qty: <strong>{r.quantity}</strong> · Requested {fmt(r.requestedAt)}
                    </p>
                    {r.replyMessage && (
                      <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1 mt-1.5">
                        <strong>Store:</strong> {r.replyMessage}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={r.status} />
                    {r.status === 'pending' && (
                      <button className="text-red-500 hover:text-red-700 text-xs font-medium" onClick={() => handleWithdrawRequest(r.id)}>
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-700 bg-indigo-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">

          {/* ════ OVERVIEW ═══════════════════════════════════════════════════ */}
          {tab === 'overview' && (
            <div className="space-y-6">

              {/* Active materials */}
              <div>
                <SectionLabel title={`Active Materials — ${activeKits.length}`} />
                {activeKits.length === 0 ? <Empty text="No materials currently issued" /> : (
                  <div className="space-y-2">
                    {activeKits.map((k) => {
                      const outstanding    = k.quantity - (k.returnedQuantity || 0);
                      const expired        = k.validityEndsAt && new Date(k.validityEndsAt) < new Date();
                      const isPaid         = (k._count?.salaryDeductions || 0) > 0;
                      return (
                        <div key={k.id} className="flex items-start justify-between p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-sm text-gray-900">{k.kit?.name}</p>
                              {isPaid && <Badge label="Paid Item" color="amber" />}
                            </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                              Qty: <strong>{outstanding}</strong> out · Issued {fmt(k.issuedDate)}
                              {k.issuedByUsername ? ` · by ${k.issuedByUsername}` : ''}
                            </p>
                            {k.materialUnit && (
                              <p className="text-xs mt-0.5">
                                Material ID:{' '}
                                <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(k.materialUnit.id)}>
                                  {k.materialUnit.materialIdCode}
                                </button>
                              </p>
                            )}
                            {k.validityEndsAt && (
                              <p className={`text-xs mt-0.5 font-medium ${expired ? 'text-orange-600' : 'text-indigo-600'}`}>
                                {expired ? `Validity expired ${fmt(k.validityEndsAt)}` : `Valid until ${fmt(k.validityEndsAt)}`}
                              </p>
                            )}
                          </div>
                          <Badge label="Active" color="green" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Active stationery */}
              <div>
                <SectionLabel title={`Active Stationery — ${activeStat.length}`} />
                {activeStat.length === 0 ? <Empty text="No stationery currently issued" /> : (
                  <div className="space-y-2">
                    {activeStat.map((s) => {
                      const isDurable = s.item?.category === 'DURABLE';
                      const isPaid    = (s._count?.deductions || 0) > 0;
                      return (
                        <div key={s.id} className={`flex items-start justify-between p-3 rounded-lg border ${isDurable ? 'border-purple-100 bg-purple-50' : 'border-green-100 bg-green-50'}`}>
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-sm text-gray-900">{s.item?.name}</p>
                              <Badge label={isDurable ? 'Durable' : 'Consumable'} color={isDurable ? 'purple' : 'green'} />
                              {isPaid && <Badge label="Paid Item" color="amber" />}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Qty: <strong>{s.quantity}</strong> {s.item?.unit} · Issued {fmt(s.issuedDate)}
                              {s.site?.name ? ` · ${s.site.name}` : ''}
                            </p>
                            {s.materialUnit && (
                              <p className="text-xs mt-0.5">
                                Material ID:{' '}
                                <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(s.materialUnit.id)}>
                                  {s.materialUnit.materialIdCode}
                                </button>
                              </p>
                            )}
                          </div>
                          <Badge label="Active" color="green" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════ MATERIALS ══════════════════════════════════════════════════ */}
          {tab === 'materials' && (
            <div className="space-y-5">
              {allKits.length === 0 ? <Empty text="No material issuances on record" /> : (
                <>
                  {activeKits.length > 0 && (
                    <div>
                      <SectionLabel title={`Currently Active — ${activeKits.length}`} />
                      <KitTable kits={activeKits} onViewMaterialHistory={openMaterialHistory} />
                    </div>
                  )}
                  {returnedKits.length > 0 && (
                    <div>
                      <SectionLabel title={`Returned / History — ${returnedKits.length}`} />
                      <KitTable kits={returnedKits} showReturn onViewMaterialHistory={openMaterialHistory} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════ STATIONERY ═════════════════════════════════════════════════ */}
          {tab === 'stationery' && (
            <div className="space-y-5">
              {allStat.length === 0 ? <Empty text="No stationery issuances on record" /> : (
                <>
                  {activeStat.length > 0 && (
                    <div>
                      <SectionLabel title={`Currently Active — ${activeStat.length}`} />
                      <StatTable rows={activeStat} onViewMaterialHistory={openMaterialHistory} />
                    </div>
                  )}
                  {returnedStat.length > 0 && (
                    <div>
                      <SectionLabel title={`Returned / History — ${returnedStat.length}`} />
                      <StatTable rows={returnedStat} showReturn onViewMaterialHistory={openMaterialHistory} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════ DEDUCTIONS ═════════════════════════════════════════════════ */}
          {tab === 'deductions' && (
            <div>
              {deductions.length === 0 ? <Empty text="No salary deductions recorded" /> : (
                <>
                  {/* Always-visible totals — independent of the cycle filter below */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                      <p className="text-xs font-medium text-indigo-600">Current Cycle</p>
                      <p className="text-xl font-bold text-indigo-800">{currency(currentCycleTotal)}</p>
                      <p className="text-xs text-indigo-400 mt-0.5">{deductionCycles[0]?.label}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                      <p className="text-xs font-medium text-red-600">All-Time Total</p>
                      <p className="text-xl font-bold text-red-800">{currency(totalDeductions)}</p>
                      <p className="text-xs text-red-400 mt-0.5">{deductions.length} record{deductions.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Cycle filter */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 font-medium mb-1 block">View</label>
                    <select
                      className="input w-full sm:w-72"
                      value={deductionCycleFilter}
                      onChange={(e) => setDeductionCycleFilter(e.target.value)}
                    >
                      <option value="current">Current Cycle ({deductionCycles[0]?.label})</option>
                      {deductionCycles.slice(1).map((c) => (
                        <option key={c.cycleStart} value={c.cycleStart}>{c.label}</option>
                      ))}
                      <option value="all">All-Time</option>
                    </select>
                  </div>

                  {displayedDeductions.length === 0 ? <Empty text="No salary deductions for this period" /> : (
                  <div className="space-y-2">
                    {displayedDeductions.map((d) => {
                      const item = d.workerKit?.kit?.name || d.stationeryIssuance?.item?.name || '—';
                      const typeColor = d.deductionType === 'DAMAGE' ? 'red' : d.deductionType === 'LOSS' ? 'orange' : 'blue';
                      return (
                        <div key={d.id} className="flex items-start justify-between p-3 rounded-lg border border-red-100 bg-red-50">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-sm text-gray-900">{item}</p>
                              <Badge label={d.deductionType} color={typeColor} />
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{d.reason}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {fmt(d.cycleStart)}{d.cycleEnd ? ` – ${fmt(d.cycleEnd)}` : ''}
                            </p>
                          </div>
                          <p className="text-base font-bold text-red-600 flex-shrink-0 ml-4">{currency(d.amount)}</p>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Transactions section — separate from the tabbed card above ──────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Transactions</h2>
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
            {transactions.length}
          </span>
        </div>
        <div className="p-5">
          {transactions.length === 0 ? <Empty text="No transactions on record" /> : (
            <TransactionsTable
              rows={transactions}
              scrollRef={txScrollRef}
              onMouseDown={handleTxMouseDown}
              onMouseMove={handleTxMouseMove}
              onMouseUp={handleTxMouseUp}
              onViewMaterialHistory={openMaterialHistory}
            />
          )}
        </div>
      </div>

      {/* ── Report Wrong Site modal ──────────────────────────────────────────── */}
      {siteCorrectionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Report Wrong Site</h3>
              <button onClick={() => setSiteCorrectionOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            <form onSubmit={handleSiteCorrectionSubmit} className="space-y-4">
              <div>
                <label className="label">Select your current site *</label>
                <SearchableSelect
                  options={sites
                    .filter((s) => !siteAssignments.some((a) => a.siteId === s.id))
                    .map((s) => ({ id: s.id, label: s.name }))}
                  value={claimedSiteId}
                  onChange={setClaimedSiteId}
                  placeholder="Search site…"
                  required
                />
              </div>
              {siteCorrectionError && <p className="text-red-600 text-sm">{siteCorrectionError}</p>}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" className="btn-secondary" onClick={() => setSiteCorrectionOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={!claimedSiteId || siteCorrectionSaving}>
                  {siteCorrectionSaving ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={siteCorrectionConfirmOpen}
        title="Report Wrong Site"
        message={`Report your actual site as ${sites.find((s) => String(s.id) === String(claimedSiteId))?.name || 'this site'}? This will be reviewed before your site is changed.`}
        confirmLabel={siteCorrectionSaving ? 'Submitting…' : 'Report'}
        danger={false}
        onConfirm={handleSiteCorrectionConfirmed}
        onCancel={() => setSiteCorrectionConfirmOpen(false)}
      />

      {showNewRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Request Material / Stationery{hasPermission('it_view') ? ' / IT' : ''}</h3>
              <button onClick={() => setShowNewRequest(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            <form onSubmit={handleCreateRequest} className="space-y-4">
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                {[['MATERIAL', 'Material'], ['STATIONERY', 'Stationery'], ...(hasPermission('it_view') ? [['IT', 'IT']] : [])].map(([v, l]) => (
                  <button
                    key={v} type="button"
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${newRequestForm.itemKind === v ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500'}`}
                    onClick={() => { setNewRequestForm({ ...newRequestForm, itemKind: v, itemId: '' }); setItemSearchQuery(''); }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <label className="label">Item *</label>
                <input
                  className="input mb-2"
                  type="text"
                  placeholder="Search items…"
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                />
                <select className="input" value={newRequestForm.itemId} onChange={(e) => setNewRequestForm({ ...newRequestForm, itemId: e.target.value })} required size={6}>
                  <option value="">Select item…</option>
                  {(newRequestForm.itemKind === 'MATERIAL' ? catalogMaterials : newRequestForm.itemKind === 'STATIONERY' ? catalogStationery : catalogIT)
                    .filter((it) => it.name.toLowerCase().includes(itemSearchQuery.trim().toLowerCase()))
                    .map((it) => (
                      <option key={it.id} value={it.id}>{it.name}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="label">Quantity</label>
                <input className="input" type="number" min="1" value={newRequestForm.quantity} onChange={(e) => setNewRequestForm({ ...newRequestForm, quantity: e.target.value })} />
              </div>
              <div>
                <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
                <input className="input" value={newRequestForm.remarks} onChange={(e) => setNewRequestForm({ ...newRequestForm, remarks: e.target.value })} />
              </div>
              {newRequestError && <p className="text-red-600 text-sm">{newRequestError}</p>}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" className="btn-secondary" onClick={() => setShowNewRequest(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={newRequestSaving || !newRequestForm.itemId}>
                  {newRequestSaving ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Material ID lifecycle history modal ──────────────────────────────── */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Material History</h3>
                {historyModal !== 'loading' && (
                  <p className="text-sm text-gray-500 mt-0.5">{historyModal.unit?.materialIdCode}</p>
                )}
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            {historyModal === 'loading' ? (
              <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name} · {historyModal.unit?.site?.name}
                </p>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {historyModal.events.map((e) => (
                    <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                        {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                        {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                      </div>
                      <p className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(e.occurredAt).toLocaleString('en-GB')}
                      </p>
                    </div>
                  ))}
                  {historyModal.events.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
                  )}
                </div>
                <MaterialInfoPanel materialUnitId={historyModal.unit?.id} unitKitId={historyModal.unit?.kitId} unitItItemId={historyModal.unit?.itItemId} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transactions table ───────────────────────────────────────────────────────
const TransactionsTable = ({ rows, scrollRef, onMouseDown, onMouseMove, onMouseUp, onViewMaterialHistory }) => (
  <div
    ref={scrollRef}
    className="overflow-x-auto select-none cursor-grab active:cursor-grabbing"
    onMouseDown={onMouseDown}
    onMouseMove={onMouseMove}
    onMouseUp={onMouseUp}
    onMouseLeave={onMouseUp}
  >
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
          <th className="pb-2 font-medium">Date</th>
          <th className="pb-2 font-medium">Site</th>
          <th className="pb-2 font-medium">Type</th>
          <th className="pb-2 font-medium">Item</th>
          <th className="pb-2 font-medium text-right">Qty</th>
          <th className="pb-2 font-medium text-right">Value</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 font-medium">Performed By</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.map((t) => {
          const cfg = TX_TYPE_CFG[t.txType] || { label: t.txType, color: 'gray' };
          return (
            <tr key={t.id}>
              <td className="py-2.5 text-gray-500 whitespace-nowrap">{fmtDateTime(t.date)}</td>
              <td className="py-2.5 text-gray-600">{t.site || '—'}</td>
              <td className="py-2.5 whitespace-nowrap"><Badge label={cfg.label} color={cfg.color} /></td>
              <td className="py-2.5 text-gray-800 font-medium">
                {t.itemName || '—'}
                {t.materialUnitId && (
                  <button
                    className="block font-mono text-xs text-indigo-600 hover:underline font-normal"
                    onClick={() => onViewMaterialHistory(t.materialUnitId)}
                  >
                    {t.materialIdCode}
                  </button>
                )}
              </td>
              <td className="py-2.5 text-right font-medium text-gray-700">{t.quantity ?? '—'}</td>
              <td className="py-2.5 text-right text-gray-600 whitespace-nowrap">{t.value > 0 ? currency(t.value) : '—'}</td>
              <td className="py-2.5 whitespace-nowrap"><StatusBadge status={t.status} /></td>
              <td className="py-2.5 text-gray-500">{t.performedBy || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ── Kit table ─────────────────────────────────────────────────────────────────
const KitTable = ({ kits, showReturn = false, onViewMaterialHistory }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
          <th className="pb-2 font-medium">Material</th>
          <th className="pb-2 font-medium">Qty</th>
          <th className="pb-2 font-medium">Issued</th>
          {showReturn && <th className="pb-2 font-medium">Returned</th>}
          {showReturn && <th className="pb-2 font-medium">Condition</th>}
          {!showReturn && <th className="pb-2 font-medium">Valid Until</th>}
          <th className="pb-2 font-medium">Issued By</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {kits.map((k) => {
          const outstanding = k.quantity - (k.returnedQuantity || 0);
          const expired     = k.validityEndsAt && new Date(k.validityEndsAt) < new Date();
          const isPaid      = (k._count?.salaryDeductions || 0) > 0;
          return (
            <tr key={k.id} className={showReturn ? 'text-gray-500' : ''}>
              <td className="py-2.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-medium ${showReturn ? 'text-gray-600' : 'text-gray-800'}`}>{k.kit?.name}</span>
                  {isPaid && <Badge label="Paid" color="amber" />}
                </div>
                {k.materialUnit && (
                  <button
                    className="font-mono text-xs text-indigo-600 hover:underline"
                    onClick={() => onViewMaterialHistory(k.materialUnit.id)}
                  >
                    {k.materialUnit.materialIdCode}
                  </button>
                )}
              </td>
              <td className="py-2.5">
                {showReturn
                  ? <span>{k.quantity} <span className="text-gray-400 text-xs">(returned {k.returnedQuantity || 0})</span></span>
                  : <span className="font-medium">{outstanding} out</span>
                }
              </td>
              <td className="py-2.5 text-gray-500 whitespace-nowrap">{fmt(k.issuedDate)}</td>
              {showReturn && (
                <td className="py-2.5 text-gray-500 whitespace-nowrap">{fmt(k.returnedDate)}</td>
              )}
              {showReturn && (
                <td className="py-2.5">
                  {k.returnCondition ? (
                    <Badge label={k.returnCondition} color={k.returnCondition === 'DAMAGED' ? 'red' : 'green'} />
                  ) : '—'}
                </td>
              )}
              {!showReturn && (
                <td className="py-2.5 whitespace-nowrap">
                  {k.validityEndsAt
                    ? <span className={expired ? 'text-orange-600 font-medium' : 'text-gray-500'}>{fmt(k.validityEndsAt)}{expired ? ' (expired)' : ''}</span>
                    : <span className="text-gray-400">No expiry</span>}
                </td>
              )}
              <td className="py-2.5 text-gray-500">{k.issuedByUsername || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ── Stationery table ──────────────────────────────────────────────────────────
const StatTable = ({ rows, showReturn = false, onViewMaterialHistory }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
          <th className="pb-2 font-medium">Item</th>
          <th className="pb-2 font-medium">Category</th>
          <th className="pb-2 font-medium">Qty</th>
          <th className="pb-2 font-medium">Issued</th>
          {showReturn && <th className="pb-2 font-medium">Returned</th>}
          {showReturn && <th className="pb-2 font-medium">Condition</th>}
          <th className="pb-2 font-medium">Site</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.map((s) => {
          const isDurable = s.item?.category === 'DURABLE';
          const isPaid    = (s._count?.deductions || 0) > 0;
          return (
            <tr key={s.id} className={showReturn ? 'text-gray-500' : ''}>
              <td className="py-2.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-medium ${showReturn ? 'text-gray-600' : 'text-gray-800'}`}>{s.item?.name}</span>
                  {isPaid && <Badge label="Paid" color="amber" />}
                </div>
                {s.materialUnit && (
                  <button
                    className="font-mono text-xs text-indigo-600 hover:underline"
                    onClick={() => onViewMaterialHistory(s.materialUnit.id)}
                  >
                    {s.materialUnit.materialIdCode}
                  </button>
                )}
              </td>
              <td className="py-2.5">
                <Badge label={isDurable ? 'Durable' : 'Consumable'} color={isDurable ? 'purple' : 'green'} />
              </td>
              <td className="py-2.5">
                {s.quantity} {s.item?.unit}
                {showReturn && s.returnedQty ? <span className="text-gray-400 text-xs ml-1">(ret. {s.returnedQty})</span> : ''}
              </td>
              <td className="py-2.5 text-gray-500 whitespace-nowrap">{fmt(s.issuedDate)}</td>
              {showReturn && (
                <td className="py-2.5 text-gray-500 whitespace-nowrap">{fmt(s.returnedDate)}</td>
              )}
              {showReturn && (
                <td className="py-2.5">
                  {s.repairStatus ? (
                    <Badge
                      label={s.repairStatus === 'UNDER_REPAIR' ? 'Repaired' : s.repairStatus === 'SCRAPPED' ? 'Scrapped' : s.repairStatus}
                      color={s.repairStatus === 'SCRAPPED' ? 'red' : 'green'}
                    />
                  ) : s.returnCondition ? (
                    <Badge label={s.returnCondition} color={s.returnCondition === 'DAMAGED' ? 'red' : 'green'} />
                  ) : '—'}
                </td>
              )}
              <td className="py-2.5 text-gray-500">{s.site?.name || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const BADGE_COLORS = {
  green:  'bg-green-100 text-green-700',
  red:    'bg-red-100 text-red-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  purple: 'bg-purple-100 text-purple-700',
  amber:  'bg-amber-100 text-amber-700',
  blue:   'bg-blue-100 text-blue-700',
  orange: 'bg-orange-100 text-orange-700',
  gray:   'bg-gray-100 text-gray-500',
};

const Badge = ({ label, color = 'gray' }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_COLORS[color] || BADGE_COLORS.gray}`}>
    {label}
  </span>
);

const StatCard = ({ label, value, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    green:  'bg-green-50 text-green-700 border-green-100',
    red:    'bg-red-50 text-red-700 border-red-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium mt-0.5 opacity-70">{label}</p>
    </div>
  );
};

const SectionLabel = ({ title }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">{title}</p>
);

const Empty = ({ text }) => (
  <p className="text-center text-gray-400 text-sm py-8">{text}</p>
);
